import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FaSyncAlt } from 'react-icons/fa';
import { Input } from '../../../components/forms/Input';
import { Textarea } from '../../../components/forms/Textarea';
import { usePasStore } from '../pasStore';
import type { Policy } from '../types';
import { formatCurrency, formatDate } from '../utils';
import { Button } from './Button';
import { Modal } from './Modal';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  policy: Policy;
}

export const RenewPolicyModal: React.FC<Props> = ({ isOpen, onClose, policy }) => {
  const navigate = useNavigate();
  const renewPolicy = usePasStore((s) => s.renewPolicy);

  const [effectiveDate, setEffectiveDate] = useState(policy.expirationDate);
  const [adjustment, setAdjustment] = useState('6');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const pct = Number(adjustment) || 0;
  const newPremium = Math.round(policy.annualPremium * (1 + pct / 100));

  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const renewal = await renewPolicy(policy.id, {
        effectiveDate,
        annualPremium: newPremium,
        note: note.trim() ? note.trim() : `Renewed at ${pct >= 0 ? '+' : ''}${pct}% adjustment.`,
      });
      onClose();
      navigate(`/policy-admin/policies/${renewal.id}`);
    } catch {
      // The store surfaces the error as a toast; keep the modal open for a retry.
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Renew policy"
      subtitle={`${policy.policyNumber} · ${policy.insured.name}`}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} loading={submitting} leftIcon={<FaSyncAlt className="h-3 w-3" />}>
            Create renewal
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-3 rounded-lg border p-4" style={{ borderColor: 'var(--color-border-light)' }}>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
              Expiring term
            </p>
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {formatDate(policy.effectiveDate)} – {formatDate(policy.expirationDate)}
            </p>
          </div>
          <div>
            <p className="text-[11px] uppercase tracking-wide" style={{ color: 'var(--color-text-secondary)' }}>
              Expiring premium
            </p>
            <p className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              {formatCurrency(policy.annualPremium)}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Input label="New effective date" type="date" value={effectiveDate} onChange={(e) => setEffectiveDate(e.target.value)} fullWidth required />
          <Input label="Premium adjustment (%)" type="number" value={adjustment} onChange={(e) => setAdjustment(e.target.value)} fullWidth helperText="Applied to the expiring premium" />
        </div>

        <div className="rounded-lg border p-4" style={{ backgroundColor: 'var(--color-success-50)', borderColor: 'var(--color-success-100)' }}>
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium" style={{ color: 'var(--color-success-800)' }}>
              Renewal annual premium
            </span>
            <span className="text-lg font-bold" style={{ color: 'var(--color-success-800)' }}>
              {formatCurrency(newPremium)}
            </span>
          </div>
        </div>

        <Textarea label="Renewal note" value={note} onChange={(e) => setNote(e.target.value)} rows={3} placeholder="Optional note for the renewal…" fullWidth />
      </div>
    </Modal>
  );
};
